import { SEO } from "../components/common/SEO";
import { FullContactForm } from "../components/forms/FullContactForm";
import { useLanguage } from "../context/LanguageProvider";
import { useSiteData } from "../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../lib/i18n";
import { whatsappHref } from "../lib/format";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";

export default function Contact() {
    const { lang } = useLanguage();
    const { contactInfo, siteContent } = useSiteData();
    const seo = siteContent?.seo?.contact || {};

    return (
        <div data-testid="page-contact" className="bg-ink">
            <SEO
                title={pick(seo.title, lang) || "Contact"}
                description={pick(seo.description, lang)}
                lang={lang}
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 md:py-20">
                <div className="max-w-3xl">
                    <div className="eyebrow mb-3">
                        {pick(UI_STRINGS.nav.contact, lang)}
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight text-neutral-50">
                        {pick(
                            {
                                fr: "Écrire au cabinet, ou nous joindre directement",
                                en: "Write to the firm, or reach us directly",
                            },
                            lang,
                        )}
                    </h1>
                    <p className="mt-6 text-neutral-300 text-lg leading-relaxed">
                        {pick(
                            {
                                fr: "Pour toute question ou pour prendre rendez-vous, utilisez le formulaire ou joignez-nous par téléphone, WhatsApp ou courriel.",
                                en: "For any question or to book an appointment, use the form or reach us by phone, WhatsApp or email.",
                            },
                            lang,
                        )}
                    </p>
                </div>
            </section>

            <section
                id="form"
                className="mx-auto max-w-7xl px-5 lg:px-10 pb-16 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10"
            >
                {/* Left: contact info + WhatsApp buttons */}
                <aside
                    data-testid="contact-info-block"
                    className="lg:col-span-5 lg:sticky lg:top-28 self-start"
                >
                    <div className="card-line p-8">
                        <ul className="space-y-6 text-sm text-neutral-200">
                            <li className="flex items-start gap-4">
                                <MapPin
                                    size={18}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <div>
                                    <div className="eyebrow mb-1 text-[0.65rem]">
                                        {pick(
                                            { fr: "Adresse", en: "Address" },
                                            lang,
                                        )}
                                    </div>
                                    <div data-testid="contact-address">
                                        {contactInfo &&
                                            pick(contactInfo.address, lang)}
                                    </div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <Phone
                                    size={18}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <div>
                                    <div className="eyebrow mb-1 text-[0.65rem]">
                                        {pick(
                                            { fr: "Téléphone", en: "Phone" },
                                            lang,
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {contactInfo?.phones.map((p) => (
                                            <a
                                                key={p}
                                                href={`tel:${p.replace(/\s+/g, "")}`}
                                                className="hover:text-gold transition-colors"
                                                data-testid={`contact-phone-${p.replace(/[^\d]/g, "")}`}
                                            >
                                                {p}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <Mail
                                    size={18}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <div>
                                    <div className="eyebrow mb-1 text-[0.65rem]">
                                        {pick(
                                            { fr: "Courriel", en: "Email" },
                                            lang,
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {contactInfo?.email_main && (
                                            <a
                                                href={`mailto:${contactInfo.email_main}`}
                                                className="hover:text-gold transition-colors break-all"
                                                data-testid="contact-email-main"
                                            >
                                                {contactInfo.email_main}
                                            </a>
                                        )}
                                        {contactInfo?.email_cross_border && (
                                            <a
                                                href={`mailto:${contactInfo.email_cross_border}`}
                                                className="hover:text-gold transition-colors break-all block"
                                                data-testid="contact-email-cross"
                                            >
                                                {contactInfo.email_cross_border}
                                                <span className="block text-neutral-500 text-xs mt-1">
                                                    {pick(
                                                        UI_STRINGS.misc
                                                            .cross_border_label,
                                                        lang,
                                                    )}
                                                </span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <Clock
                                    size={18}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <div>
                                    <div className="eyebrow mb-1 text-[0.65rem]">
                                        {pick(
                                            UI_STRINGS.sections.opening_hours,
                                            lang,
                                        )}
                                    </div>
                                    <ul className="text-neutral-300 space-y-1">
                                        {contactInfo?.opening_hours.map(
                                            (h, i) => (
                                                <li
                                                    key={i}
                                                    className="flex gap-3 justify-between max-w-xs"
                                                >
                                                    <span>
                                                        {pick(h.day, lang)}
                                                    </span>
                                                    <span className="text-neutral-400">
                                                        {pick(h.hours, lang)}
                                                    </span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            </li>
                        </ul>

                        <div className="mt-8 grid grid-cols-1 gap-3">
                            {contactInfo?.whatsapp_local && (
                                <a
                                    href={whatsappHref(
                                        contactInfo.whatsapp_local,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-whatsapp"
                                    data-testid="contact-whatsapp-local"
                                >
                                    <MessageCircle
                                        size={18}
                                        className="wa-icon"
                                    />
                                    {pick(
                                        UI_STRINGS.misc.whatsapp_local,
                                        lang,
                                    )}{" "}
                                    · {contactInfo.whatsapp_local}
                                </a>
                            )}
                            {contactInfo?.whatsapp_canada && (
                                <a
                                    href={whatsappHref(
                                        contactInfo.whatsapp_canada,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-whatsapp"
                                    data-testid="contact-whatsapp-canada"
                                >
                                    <MessageCircle
                                        size={18}
                                        className="wa-icon"
                                    />
                                    {pick(
                                        UI_STRINGS.misc.whatsapp_canada,
                                        lang,
                                    )}{" "}
                                    · {contactInfo.whatsapp_canada}
                                </a>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Right: full form */}
                <div className="lg:col-span-7">
                    <div className="card-line p-6 md:p-10">
                        <div className="eyebrow mb-2">
                            {pick(UI_STRINGS.misc.write_to_us, lang)}
                        </div>
                        <h2 className="font-serif text-2xl md:text-3xl text-neutral-50 leading-tight mb-8">
                            {pick(
                                {
                                    fr: "Un formulaire simple, confidentiel.",
                                    en: "A simple, confidential form.",
                                },
                                lang,
                            )}
                        </h2>
                        <FullContactForm />
                    </div>
                </div>
            </section>

            {/* Map */}
            <section
                data-testid="contact-map"
                className="border-t border-ink-border"
            >
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-14">
                    <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <div className="eyebrow mb-2">
                                {pick(UI_STRINGS.sections.find_us, lang)}
                            </div>
                            <h2 className="font-serif text-2xl md:text-3xl text-neutral-50">
                                {pick(UI_STRINGS.sections.map_note, lang)}
                            </h2>
                        </div>
                    </div>
                    <div className="w-full aspect-[16/9] md:aspect-[21/9] border border-ink-border overflow-hidden bg-ink-surface">
                        <iframe
                            title="Cabinet MABS — Bobo-Dioulasso"
                            src="https://maps.google.com/maps?q=Bobo-Dioulasso%20Sarfalao&t=&z=14&ie=UTF8&iwloc=&output=embed"
                            width="100%"
                            height="100%"
                            style={{ border: 0, filter: "grayscale(0.4) invert(0.08)" }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="contact-map-iframe"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
